import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiHeart, FiBookOpen, FiUsers, FiStar } = FiIcons;

const Mission = () => {
  const coreValues = [
    {
      icon: FiHeart,
      title: 'Compassion',
      description: 'We approach our work with empathy and understanding, recognizing the unique challenges faced by young people in our communities.'
    },
    {
      icon: FiBookOpen,
      title: 'Education',
      description: 'We believe quality education is a right, not a privilege. We are committed to making learning accessible, engaging, and impactful.'
    },
    {
      icon: FiUsers,
      title: 'Community',
      description: 'We foster meaningful connections and collaborative learning environments where students support each other\'s growth.'
    },
    {
      icon: FiStar,
      title: 'Excellence',
      description: 'We strive for excellence in all aspects of our work, continuously improving our programs to deliver the best outcomes.'
    }
  ];

  return (
    <div className="space-y-8 pb-16 md:pb-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiTarget} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Mission</h1>
          <div className="max-w-3xl mx-auto">
            <p className="text-lg md:text-xl text-gray-700 italic font-medium mb-6">
              "To empower young people in Sierra Leone and The Gambia through inspirational education, 
              mentorship, and leadership development, creating a generation of confident, skilled leaders 
              who will transform their communities."
            </p>
            <p className="text-gray-600 text-sm md:text-base">
              At ILM Global, we are dedicated to breaking down barriers to education and providing 
              young people with the tools, knowledge, and support they need to thrive in an increasingly 
              complex world. We believe that education is the foundation upon which sustainable 
              development and lasting change are built.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Mission Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
        >
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center">
            <SafeIcon icon={FiTarget} className="w-6 h-6 mr-2 text-primary-600" />
            What We Do
          </h2>
          <div className="space-y-4 text-gray-600 text-sm md:text-base">
            <p>
              Through our educational platform, we provide access to high-quality learning materials, 
              interactive quizzes, and study resources tailored to local curricula. Our focus areas include:
            </p>
            <ul className="space-y-2 list-disc pl-5">
              <li><strong>Academic Excellence:</strong> Supporting core subjects with comprehensive study materials</li>
              <li><strong>Digital Literacy:</strong> Equipping students with essential technology skills</li>
              <li><strong>Leadership Development:</strong> Fostering the next generation of community leaders</li>
              <li><strong>Peer Learning:</strong> Creating collaborative spaces for knowledge sharing</li>
              <li><strong>Mentorship:</strong> Connecting students with role models and mentors</li>
            </ul>
            <p>
              We work closely with schools, communities, and government agencies to ensure our programs 
              address real needs and create meaningful impact.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
        >
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center">
            <SafeIcon icon={FiBookOpen} className="w-6 h-6 mr-2 text-primary-600" />
            How We Work
          </h2>
          <div className="space-y-4 text-gray-600 text-sm md:text-base">
            <p>
              Our approach is built on these key principles:
            </p>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="bg-primary-100 p-2 rounded-full mr-3 mt-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">Accessibility</h3>
                  <p>Making education available to all, regardless of location or economic background</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-primary-100 p-2 rounded-full mr-3 mt-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">Localization</h3>
                  <p>Tailoring content to reflect local context, cultures, and educational needs</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-primary-100 p-2 rounded-full mr-3 mt-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">Collaboration</h3>
                  <p>Working with local partners to enhance impact and sustainability</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-primary-100 p-2 rounded-full mr-3 mt-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">Innovation</h3>
                  <p>Continuously improving our methods and embracing technology to enhance learning</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Core Values */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Our Core Values</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {coreValues.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow"
            >
              <div className="bg-primary-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={value.icon} className="w-7 h-7 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 text-center mb-2">{value.title}</h3>
              <p className="text-gray-600 text-sm text-center">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-6 md:p-8 text-white text-center"
      >
        <h2 className="text-2xl font-bold mb-4">Join Our Mission</h2>
        <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
          Together, we can create a brighter future for young people in West Africa. 
          Whether you are a student, educator, volunteer, or donor, there is a place for you in our movement.
        </p>
        <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
          <button className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors">
            Become a Volunteer
          </button>
          <button className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-primary-600 transition-colors">
            Support Our Work
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Mission;